import { motion } from "framer-motion";
import {
  Smartphone,
  Radio,
  Shield,
  BarChart3,
  FileCheck,
  Activity,
  Database,
  Lock,
  Cpu,
  Server,
  Cloud,
  Zap,
  Globe,
  Eye,
  QrCode,
  MapPin,
  TrendingUp,
  CheckCircle2,
  Award,
} from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

export function Technology() {
  const features = [
    {
      icon: Smartphone,
      title: "100% Origin Trace Logging",
      description: "Every feedstock unit tracked from source to destination with complete transparency",
      color: "from-cyan-500 to-blue-600",
    },
    {
      icon: Lock,
      title: "Tamper-Proof Records",
      description: "Blockchain-style immutable movement and volume records ensure data integrity",
      color: "from-purple-500 to-indigo-600",
    },
    {
      icon: Radio,
      title: "IoT-Enabled Collection",
      description: "Smart sensors and IoT devices monitor collection points in real-time",
      color: "from-green-500 to-emerald-600",
    },
    {
      icon: QrCode,
      title: "Digital Quality Tagging",
      description: "Each batch tagged with quality metrics for certification compliance",
      color: "from-orange-500 to-red-600",
    },
  ];

  const platformFeatures = [
    { icon: MapPin, title: "Real-Time GPS Tracking", description: "Live location tracking of all collection vehicles" },
    { icon: Database, title: "Automated Reconciliation", description: "AI-powered data verification across points" },
    { icon: FileCheck, title: "Digital Documentation", description: "Paperless onboarding and contracts" },
    { icon: Activity, title: "Carbon Credits", description: "Automated GHG savings calculation" },
  ];

  return (
    <section id="technology" className="py-20 lg:py-32 bg-gradient-to-br from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="techGrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#techGrid)" />
        </svg>
      </div>

      {/* Decorative Blobs */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#7CB342]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-[#1976D2]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342]/10 to-[#1976D2]/10 border-2 border-[#7CB342]/30 rounded-full px-5 py-2.5 mb-6"
          >
            <Cpu className="w-5 h-5 text-[#7CB342]" />
            <span className="text-sm font-bold text-gray-900">Digital Traceability Platform</span>
          </motion.div>

          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Introducing{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
                B-Trace
              </span>
              <motion.div
                animate={{ scaleX: [0, 1] }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-[#7CB342] to-[#1976D2] origin-left"
              />
            </span>
          </h2>

          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            Proprietary IoT-powered digital traceability application for complete feedstock ecosystem management
          </p>
        </motion.div>

        {/* Main Platform Showcase */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20 items-center">
          {/* Left - Platform Visual */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative">
              {/* Main Card */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-3xl opacity-30 blur-xl" />
              <div className="relative bg-white rounded-3xl p-3 shadow-2xl border-2 border-gray-100">
                {/* Mobile App Mockup */}
                <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1703817855495-718f7e1d7f6f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzbWFydHBob25lJTIwc3VzdGFpbmFiaWxpdHklMjB0cmFja2luZ3xlbnwxfHx8fDE3NjUwOTIzMDV8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                    alt="B-Trace Mobile Application"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent" />
                  
                  {/* App Interface Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="bg-white/95 backdrop-blur-md rounded-xl p-4 shadow-lg">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-gradient-to-br from-[#7CB342] to-[#1976D2] rounded-lg p-2">
                          <Smartphone className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="text-xs font-bold text-gray-600">UCO Collection</div>
                          <div className="text-lg font-black text-gray-900">Live Tracking Active</div>
                        </div>
                        <CheckCircle2 className="w-6 h-6 text-[#7CB342]" />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: "Today", value: "245L" },
                          { label: "This Week", value: "1.2K L" },
                          { label: "Quality", value: "A+" },
                        ].map((stat, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-2 text-center">
                            <div className="text-xs text-gray-600 font-semibold">{stat.label}</div>
                            <div className="text-sm font-black text-gray-900">{stat.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating UCO Badge */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-6 -right-6 bg-gradient-to-br from-[#7CB342] to-[#689F38] border-4 border-white rounded-2xl p-4 shadow-2xl"
              >
                <div className="text-center">
                  <div className="text-xs font-bold text-white/80 mb-1">UCO Certified</div>
                  <div className="text-2xl font-black text-white">100%</div>
                </div>
              </motion.div>

              {/* IoT Badge */}
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 bg-gradient-to-br from-[#1976D2] to-[#1565C0] border-4 border-white rounded-2xl p-4 shadow-2xl"
              >
                <div className="flex items-center gap-2">
                  <Radio className="w-6 h-6 text-white" />
                  <div>
                    <div className="text-xs font-bold text-white/80">IoT</div>
                    <div className="text-sm font-black text-white">Enabled</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right - Platform Description */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-full px-6 py-3 shadow-lg">
              <Zap className="w-5 h-5 text-white" />
              <span className="text-white font-bold">Powered by IoT & AI</span>
            </div>

            <h3 className="text-3xl lg:text-4xl font-black text-gray-900 leading-tight">
              Complete Digital Ecosystem for{" "}
              <span className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
                UCO Traceability
              </span>
            </h3>

            <p className="text-lg text-gray-600 leading-relaxed">
              B-Trace is our proprietary platform that brings <span className="font-bold text-gray-900">100% transparency</span> to the 
              Used Cooking Oil supply chain through IoT sensors, GPS tracking, and AI-powered verification. From collection to 
              certification, every litre is digitally tracked.
            </p>

            {/* Platform Features Grid */}
            <div className="grid grid-cols-2 gap-4">
              {platformFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-xl opacity-0 group-hover:opacity-50 blur transition-all" />
                  <div className="relative bg-white border-2 border-gray-200 group-hover:border-transparent rounded-xl p-4 transition-all">
                    <feature.icon className="w-6 h-6 text-[#7CB342] mb-2 group-hover:scale-110 transition-transform" />
                    <div className="text-sm font-black text-gray-900 mb-1">{feature.title}</div>
                    <div className="text-xs text-gray-600">{feature.description}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Key Stats */}
            <div className="flex gap-4 pt-4">
              {[
                { icon: Eye, label: "100% Visible", color: "from-[#7CB342] to-[#689F38]" },
                { icon: Shield, label: "ISCC Ready", color: "from-[#1976D2] to-[#1565C0]" },
                { icon: TrendingUp, label: "Real-Time", color: "from-purple-500 to-indigo-600" },
              ].map((stat, index) => (
                <div key={index} className={`flex-1 bg-gradient-to-br ${stat.color} rounded-xl p-4 text-white text-center`}>
                  <stat.icon className="w-6 h-6 mx-auto mb-1" />
                  <div className="text-xs font-bold">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Core Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <h3 className="text-2xl lg:text-3xl font-black text-gray-900 text-center mb-12">
            Enterprise-Grade Features for{" "}
            <span className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
              Complete Traceability
            </span>
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.color} rounded-2xl opacity-0 group-hover:opacity-50 blur transition-all duration-500`} />
                <div className="relative bg-white border-2 border-gray-200 group-hover:border-transparent rounded-2xl p-6 h-full transition-all duration-500">
                  <div className={`bg-gradient-to-br ${feature.color} w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-lg font-black text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Data Flow Diagram */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <h3 className="text-2xl lg:text-3xl font-black text-gray-900 mb-4 text-center">
            Multi-Source Real-Time Data Integration
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            B-Trace aggregates data from multiple touchpoints to create a unified, verifiable supply chain
          </p>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Smartphone, label: "FBO Mobile Apps", gradient: "from-amber-400 to-orange-500" },
              { icon: Server, label: "Collection Fleet", gradient: "from-blue-400 to-indigo-500" },
              { icon: Cpu, label: "IoT Sensors", gradient: "from-purple-400 to-fuchsia-500" },
              { icon: Cloud, label: "Central Dashboard", gradient: "from-green-400 to-emerald-500" },
            ].map((source, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative"
              >
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${source.gradient} rounded-2xl opacity-50 group-hover:opacity-75 blur transition-all`} />
                <div className="relative bg-white rounded-2xl p-6 text-center transform group-hover:scale-105 transition-all">
                  <div className={`bg-gradient-to-br ${source.gradient} w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center shadow-lg`}>
                    <source.icon className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-sm font-bold text-gray-900">{source.label}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Creative Impact Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-[#7CB342] via-[#1976D2] to-[#7CB342] rounded-3xl opacity-20 blur-2xl" />
            <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 lg:p-12 shadow-xl overflow-hidden">
              {/* Decorative Background Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#7CB342]/10 to-transparent rounded-full -mr-32 -mt-32" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#1976D2]/10 to-transparent rounded-full -ml-24 -mb-24" />

              <div className="relative">
                {/* Top Badge */}
                <div className="flex justify-center mb-8">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-full px-6 py-3 shadow-lg">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                    <span className="text-sm font-bold text-white">End-to-End Traceability</span>
                  </div>
                </div>

                {/* Main Content */}
                <h4 className="text-2xl lg:text-4xl font-black text-gray-900 text-center mb-8 leading-tight">
                  Why B-Trace Matters for Your Business
                </h4>

                {/* Benefits Grid */}
                <div className="grid md:grid-cols-3 gap-6 mb-10">
                  {[
                    { icon: Shield, title: "Verifiable", desc: "Blockchain-style immutable records", color: "from-[#7CB342] to-[#689F38]" },
                    { icon: FileCheck, title: "Auditable", desc: "Complete audit trail for compliance", color: "from-[#1976D2] to-[#1565C0]" },
                    { icon: Radio, title: "IoT-Validated", desc: "Real-time sensor verification", color: "from-[#7CB342] to-[#1976D2]" },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="group relative"
                    >
                      <div className={`absolute -inset-0.5 bg-gradient-to-br ${item.color} rounded-2xl opacity-30 group-hover:opacity-50 blur transition-all`} />
                      <div className="relative bg-white rounded-2xl p-6 text-center h-full shadow-lg">
                        <div className={`bg-gradient-to-br ${item.color} w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-all`}>
                          <item.icon className="w-7 h-7 text-white" />
                        </div>
                        <h5 className="text-lg font-black text-gray-900 mb-2">{item.title}</h5>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Key Benefits List */}
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: Award, text: "Critical for sustainability certification compliance" },
                    { icon: FileCheck, text: "Meets policy mandates and regulatory requirements" },
                    { icon: TrendingUp, text: "Ensures global buyer acceptance and trust" },
                    { icon: Globe, text: "Creates tamper-proof, auditable records" },
                  ].map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-center gap-4 bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-md hover:shadow-lg transition-all group"
                    >
                      <div className="bg-gradient-to-br from-[#7CB342] to-[#1976D2] rounded-lg p-2.5 group-hover:scale-110 transition-transform">
                        <benefit.icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-semibold text-gray-700 flex-1">{benefit.text}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Bottom CTA */}
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-2xl blur opacity-50" />
                  <div className="relative bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-2xl p-8 text-center text-white">
                    <p className="text-lg lg:text-xl font-bold leading-relaxed">
                      B-Trace creates a <span className="font-black">verifiable</span>, <span className="font-black">auditable</span>, and <span className="font-black">IoT-validated</span> material flow chain - critical for sustainability certification, policy mandates, and global buyer acceptance
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}